import { fromEnv } from "./fromEnv";

export class CookieConfig {
  // To scope each cookies by a unique user, we need to use the currently logged in users id
  // to identify the cookies.
  static userId: number;

  static scopedCookieName = (key: CookieNames | string) => {
    if (key.match(/^\$_/i)) {
      return `${key}:${CookieConfig.userId}`;
    }
    return key;
  };
}

/**
 * Cookies with a $_ prefix indicates that they need to be scoped by user ID
 */
export enum CookieNames {
  AccessToken = "access_token",
  RefreshToken = "refresh_token",
  VersionCode = "version_code",
  AppId = "app_id",
  ApiLevel = "api_level",
  Language = "language",
  Tour = "$_gobiz_tour",
  // email/phone stores the last successful authentication method, so next time
  // user visits login, it will automatically show phone/email based on history
  AuthMethod = "auth_method",
  // The URL the user was trying to visit before being redirected to the login
  // page. After successful login, the user will be redirected to that page.
  DestinationBeforeLoggedOut = "destination_before_logout",
  ManualPayoutSOP = "$_manual_payout",
  PassportToken = "passport_token",
  PassportRefreshToken = "passport_refresh_token",
  HeraToken = "goid_access_token",
  HeraRefreshToken = "goid_refresh_token",
  PassportBusinessInfoUrl = "$_passport_business_info_url",
  PassportStep = "$_passport_step",
  CurrentPath = "current_path",
  SkipCor = "skip_cor",
  XsrfToken = "XSRF-TOKEN",
}

type CookieExpiry = number | "never_expired";

export const getCookie = (
  cookieName: CookieNames,
  _document = document
): string => {
  const scopedName = CookieConfig.scopedCookieName(cookieName);
  const cookie = scopedName + "=";
  const cookies = _document.cookie.split(";");
  for (let value of cookies) {
    while (value.charAt(0) === " ") {
      value = value.substring(1);
    }
    if (value.indexOf(cookie) === 0) {
      return value.substring(cookie.length, value.length);
    }
  }
  return "";
};

export const setCookie = (
  cookieName: CookieNames,
  cookieValue: string,
  expiryDays: CookieExpiry = 10 * 365,
  _document: Document = document,
  domain: string | null = null
) => {
  if (typeof expiryDays === "string") {
    expiryDays = 10 * 365;
  }

  const scopedName = CookieConfig.scopedCookieName(cookieName);
  const expiryDate = new Date();

  expiryDate.setTime(expiryDate.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  const expires = "expires=" + expiryDate.toUTCString();
  const domainPart = domain ? `;domain=${domain}` : "";
  _document.cookie =
    scopedName + "=" + cookieValue + ";" + expires + ";path=/" + domainPart;
};

export const deleteCookie = (
  cookieName: CookieNames,
  _document: Document = document,
  domain: string | null = null
) => {
  setCookie(cookieName, "", -1, _document, domain);
};

export const getBoolCookie = (cookieName: CookieNames): boolean => {
  return getCookie(cookieName) === "true";
};

export const setBoolCookie = (cookieName: CookieNames, value = true) => {
  return value ? setCookie(cookieName, "true") : deleteCookie(cookieName);
};

export const setHeraCookie = (
  cookieName: CookieNames,
  cookieValue: string,
  expiryDays: CookieExpiry = 10 * 365,
  _document: Document = document
) => {
  if (typeof expiryDays === "string") {
    expiryDays = 10 * 365;
  }

  const expiryDate = new Date();

  expiryDate.setTime(expiryDate.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  const expires = "expires=" + expiryDate.toUTCString();
  _document.cookie =
    cookieName +
    "=" +
    cookieValue +
    "; " +
    expires +
    "; domain=" +
    fromEnv("REACT_APP_HERA_COOKIE") +
    "; path=/";
};
